import { useState, useEffect, useRef } from 'react'
import { Dialog, DialogBody, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@cutilei/ui'
import { Formik } from 'formik'
import AuthService from 'services/auth'
import cutileiApi from 'services/cutileiApi'
import { getApiErrorMessage, logApiError } from 'services/apiError'
import Phone from 'services/Phone'
import CustomerService from 'services/Customer'
import ScheduleService from 'services/Schedule'
import Select from 'components/Inputs/Select'
import SelectAsync from 'components/Inputs/SelectAsync'
import NumberInput from 'components/Inputs/NumberInput'
import DateInput from 'components/Inputs/DateInput'
import CheckBox from 'components/CheckBox'
import AddCustomerModal from '../AddCustomerModal'
import EditCustomerModal from '../EditCustomerModal'
import OptionDialog from 'components/OptionDialog'
import MainButton from 'components/Buttons/MainButton'
import SecondaryButton from 'components/Buttons/SecondaryButton'
import createScheduleValidator from 'validators/createScheduleValidator'
import { ReactComponent as Loading } from 'icons/loading2.svg'
import { FaTrash, FaPlus } from 'react-icons/fa'
import { MdInfoOutline } from 'react-icons/md'
import { DateTime } from 'luxon'

import {
  Form,
  FormField,
  Input,
  DangerButton,
  Row,
  Column,
  CheckRow,
  ScheduleRow,
  InfoText,
  WarningText,
  NoticeText,
  ErrorContainer,
  Label
} from './styles'

export default function CreateScheduleModal ({
  visible,
  data = {},
  workingDays = [],
  professionals = [],
  onConfirm: handleConfirm,
  onClose: handleClose
}) {
  const { date, time, professional, customer } = data
  const formattedDate = date.toISODate ()
  const [loading, setLoading] = useState (true)
  const [errorMessage, setErrorMessage] = useState (null)
  const [customerInput, setCustomerInput] = useState ('')
  const [customerQuery, setCustomerQuery] = useState ('')
  const [showAddCustomerModal, setShowAddCustomerModal] = useState (false)
  const [showEditCustomerModal, setShowEditCustomerModal] = useState (false)
  const [showSkipDatesDialog, setShowSkipDatesDialog] = useState (false)
  const [services, setServices] = useState ([])
  const businessId = AuthService.getBusinessId ()
  const token = AuthService.getToken ()

  // Por linha: cliques seguidos na duração disparam vários pedidos, e só a resposta do último vale
  const latestTimeWindowsRequests = useRef({})

  const requestConfig = {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  }

  useEffect (() => {
    if (!visible) setCustomerQuery ('')
    getData (professional)
  }, [visible, professional])

  const getData = async (_professional = professional) => {
    setErrorMessage (null)
    
    try {
      if (_professional) {
        const { data: services } = await cutileiApi.get (
          `/businesses/${businessId}/professionals/${_professional.id}/services?cutilei_services=false`,
          requestConfig
        )
  
        setServices (services.map (service => ({
          label: service.nickname || service.name,
          value: service
        })))
      }
    } catch (error) {
      logApiError (error)
    } finally {
      setLoading (false)
    }
  }

  const getCustomers = async query => {
    try {
      const { data: customers } = await cutileiApi.get (
        `/businesses/${businessId}/customers?query=${encodeURIComponent (query)}`, requestConfig
      )

      const sortedCustomers = CustomerService.sortCustomersByQuery (customers.data, query)

      return sortedCustomers.map (customer => ({
        label: `${customer.name}${customer.phone ? ` - ${Phone.formatPhone (customer.phone)}` : ''}`,
        value: customer.id,
        data: customer
      }))
    } catch (error) {
      logApiError (error)
    }
  }

  const checkScheduleDuration = schedule => {
    const { date, duration } = schedule
    const time = schedule.time.value

    const selectedWeekday = DateTime.fromISO (date).weekday
    const workingDay = workingDays.find (day => day.iso_number === selectedWeekday)
    const parseTime = time => {
      const format = time.length > 5 ? 'HH:mm:ss' : 'H:mm'
      return DateTime.fromFormat (time, format)
    }

    const closingTime = parseTime (workingDay.closing || workingDay.work_end)
    const startTime = parseTime (time)
    const endTime = startTime.plus ({ minutes: duration })

    if (endTime > closingTime) {
      return 'A duração não deve exceder o horário de fechamento do salão'
    }

    return null
  }

  const toggleAddCustomerModal = () => setShowAddCustomerModal (!showAddCustomerModal)
  const toggleEditCustomerModal = () => setShowEditCustomerModal (!showEditCustomerModal)
  const toggleSkipDatesDialog = () => setShowSkipDatesDialog (!showSkipDatesDialog)

  return (
    <Dialog open={visible} onOpenChange={open => { if (!open) handleClose() }}>
      <DialogContent
        size='lg'
        closeOnOutsideClick={false}
        aria-describedby={undefined}
        onOpenAutoFocus={event => {
          // O foco abre na busca de cliente, como fazia o autoFocus dela
          const customerSearch = document.getElementById('create-schedule-customer')
          if (customerSearch) {
            event.preventDefault()
            customerSearch.focus()
          }
        }}
      >
        <DialogHeader>
          <DialogTitle>Agendar</DialogTitle>
        </DialogHeader>
        {loading ? <DialogBody><Loading/></DialogBody> : (
          <Formik
            validationSchema={createScheduleValidator}
            initialValues={{
              'schedules': [{
                'customer': customer ? {
                  label: customer.name,
                  value: customer.id
                } : null,
                'professional': professional ? {
                  label: professional.nickname,
                  value: professional
                } : null,
                'service': null,
                'date': formattedDate,
                'time': time ? {label: time, value: time} : null,
                'duration': 30,
                'timeWindows': []
              }],
              'addRecurrence': false,
              'recurrence': {
                frequency: ScheduleService.FREQUENCY_OPTIONS[0],
                type: {label: 'Qtd. agendamentos', value: 'scheduleCount'},
                scheduleCount: 1,
                endDate: formattedDate,
                skipDates: []
              }
            }}
            onSubmit={async (values, { setSubmitting, setFieldValue }) => {
              setErrorMessage (null)
              setShowSkipDatesDialog (false)
            
              for (const schedule of values.schedules) {
                const durationError = checkScheduleDuration (schedule)
                if (durationError) return setErrorMessage (durationError)
              }

              try {
                let createdSchedules = []
                const { recurrence } = values
                const recurrenceDates = ScheduleService.getRecurrenceDates (recurrence, formattedDate)

                for (let i = 0; i < values.schedules.length; i++) {
                  const schedule = values.schedules[i]
                  const scheduleValues = {
                    businessId,
                    date: schedule.date,
                    time: schedule.time.value,
                    duration: schedule.duration,
                    customerId: schedule.customer.value,
                    serviceId: schedule.service.value.id,
                    professionalId: schedule.professional.value.id,
                    ...values.addRecurrence && { recurrenceDates }
                  }

                  await cutileiApi.post ('/schedules/verify_availability', scheduleValues, requestConfig)
  
                  const { data: createdSchedule } = await cutileiApi.post (
                    '/schedules', scheduleValues, requestConfig
                  )

                  createdSchedules.push (createdSchedule)
                }

                setSubmitting (false)
                handleConfirm (createdSchedules)
                handleClose ()
              } catch (error) {
                logApiError (error)
                const unavailableDates = error.response?.data?.unavailableDates
                
                  if (unavailableDates) {
                    setFieldValue ('recurrence.skipDates', unavailableDates)
                  toggleSkipDatesDialog ()
                }
                else setErrorMessage (getApiErrorMessage (error))
                setSubmitting (false)
              }
            }}
          >
            {function CreateScheduleForm ({
              values,
              errors,
              touched,
              isSubmitting,
              setFieldValue,
              setFieldTouched,
              handleChange,
              handleBlur,
              handleSubmit
            }) {
              const firstSchedule = values.schedules[0]
              const firstScheduleTouched = touched.schedules?.[0]
              const firstScheduleErrors = errors.schedules?.[0]

              const resetSkipDates = () => {
                setFieldValue ('recurrence.skipDates', [])
                toggleSkipDatesDialog ()
              }

              /*
                A duração vai junto porque a lista confere conflito com ela: sem isso a API usava
                a configurada do serviço, e o que o salão encurtava para caber num vão nunca achava
                o vão. Quando só a duração muda, `autoSelect` fica desligado para não trocar o
                horário que já foi escolhido.
              */
              const getTimeWindows = async (name, professional, service, duration, { autoSelect = true } = {}) => {
                const row = name.split('.')[0]
                const request = (latestTimeWindowsRequests.current[row] ?? 0) + 1
                latestTimeWindowsRequests.current[row] = request

                try {
                  const filters = `date=${formattedDate}&business_id=${businessId}&service_id=${service.value.id}`
                                + (duration > 0 ? `&duration=${duration}` : '')

                  const { data: professionalFreeSchedules } = await cutileiApi.get(
                    `/professionals/${professional.id}/free_schedules?${filters}`, requestConfig
                  )

                  if (request !== latestTimeWindowsRequests.current[row]) return

                  const timeWindows = professionalFreeSchedules.map(timeWindow => ({
                    label: timeWindow, value: timeWindow
                  }))

                  setFieldValue(`${row}.timeWindows`, timeWindows)
                  setFieldValue(`${row}.timeWindowsLoaded`, true)
                  if (autoSelect) autoSelectTime(name, timeWindows, professional)
                } catch (error) {
                  logApiError(error)
                }
              }

              const autoSelectTime = (name, timeWindows, professional) => {
                const index = parseInt(name.match(/\d+/)?.[0] ?? '0')
                if (index === 0) return

                const prevSchedule = values.schedules[index - 1]

                if (prevSchedule?.time && prevSchedule?.service) {
                  const prevTimeStr = prevSchedule.time.value
                  // A duração da linha anterior, que pode ter sido mudada, e não a do cadastro
                  const prevDuration = prevSchedule.duration ?? prevSchedule.service.value.duration

                  const [hour, minute] = prevTimeStr.split(':').map(Number)
                  const prevDateTime = DateTime.fromObject({ hour, minute })
                  const adjDateTime = prevDateTime.plus({ minutes: prevDuration })
                  // No formato da lista, "9:00": com "HH:mm" o horário antes das 10h nunca batia
                  const adjTimeStr = adjDateTime.toFormat('H:mm')

                  /*
                    Sem horário livre logo depois, o serviço vai para o mesmo horário do anterior: é
                    o atendimento simultâneo, com outra profissional. Com a mesma profissional isso é
                    sobreposição — foi assim que uma Henna caiu às 9h em cima de um Lash, em
                    24/09/2026 —, e o horário fica em branco para ser escolhido.
                  */
                  const sameProfessional = prevSchedule.professional?.value?.id === professional.id

                  let autoTime = timeWindows.find(tw => tw.value === adjTimeStr) || null
                  if (!autoTime && !sameProfessional) {
                    autoTime = timeWindows.find(tw => tw.value === prevTimeStr) || null
                  }

                  setFieldValue(`${name.split('.')[0]}.time`, autoTime)
                }
              }

              /*
                O horário escolhido que a lista não oferece: o que veio de um clique na grade, ou
                ficou ao trocar o serviço ou a duração. O salão pode marcar por cima da própria
                agenda, então aqui só se avisa — quem decide é quem está agendando.
              */
              const isTimeOutsideFreeWindows = schedule => (
                !!schedule.time && !!schedule.timeWindowsLoaded
                && !(schedule.timeWindows ?? []).some(tw => tw.value === schedule.time.value)
              )

              const handleCustomerSelected = value => {
                setFieldValue ('schedules', values.schedules.map (schedule => ({
                  ...schedule, customer: value
                })))
              }

              const handleServiceSelected = async (name, professional, service) => {
                const index = parseInt (name.match (/\d+/)?.[0] ?? '0')

                setFieldValue (`schedules[${index}].duration`, service.value.duration)
                setFieldValue (name, service)
                if (professional) getTimeWindows(name, professional.value, service, service.value.duration)
              }

              const handleProfessionalSelected = async (name, professional, service) => {
                const index = parseInt(name.match(/\d+/)?.[0] ?? '0')

                setFieldValue (name, professional)
                getData (professional.value)
                if (service) getTimeWindows(name, professional.value, service, values.schedules[index].duration)
              }

              const handleAddSchedule = () => {
                const schedule = values.schedules[0]

                setFieldValue ('schedules', [
                  ...values.schedules,
                  {
                    customer: schedule.customer,
                    professional: schedule.professional,
                    service: null,
                    date: schedule.date,
                    time: null,
                    duration: 30
                  }
                ])
              }

              const handleDeleteSchedule = index => {
                setFieldValue (
                  'schedules',
                  values.schedules.map ((s, i) => ({...s, idx: i})).filter (s => s.idx !== index)
                )
              }

              const getNoOptionsMessage = service => (
                service
                  ? 'Nenhum horário disponível'
                  : 'Selecione o serviço para obter os horários disponíveis'
              )

              return (
                <>
                  <DialogBody>
                    <Form>
                      <Row>
                        <FormField>
                          <Input
                            name='date'
                            placeholder='Data'
                            value={DateTime.fromISO (firstSchedule.date).toFormat ('dd/MM/yyyy')}
                            onChange={handleChange ('date')} 
                            onBlur={handleBlur ('date')}
                            disabled={date !== undefined}
                          />
                        </FormField>
                        <FormField>
                          <SelectAsync
                            name='customer'
                            inputId='create-schedule-customer'
                            placeholder='Cliente...'
                            value={firstSchedule.customer}
                            loadOptions={getCustomers}
                            onChange={(_, value) => handleCustomerSelected (value)}
                            onInputChange={setCustomerInput}
                            onMenuClose={() => setCustomerQuery (customerInput)}
                            onBlur={setFieldTouched}
                            error={firstScheduleErrors?.customer}
                            touched={firstScheduleTouched?.customer}
                            onMenuOpen={() => setFieldValue ('customer', null)}
                            containerStyles={{width: 400}}
                          />
                          {firstScheduleTouched?.customer && firstScheduleErrors?.customer && (
                            <WarningText>
                              {firstScheduleErrors.customer}
                            </WarningText>
                          )}
                        </FormField>
                        <SecondaryButton
                          label={firstSchedule.customer ? 'Editar dados' : 'Novo cliente'}
                          onClick={firstSchedule.customer ? toggleEditCustomerModal : toggleAddCustomerModal}
                          style={{alignSelf: 'flex-start'}}
                          width={135}
                        />
                      </Row>
                      {values.schedules.map ((schedule, index) => (
                        <Column key={index}>
                          <ScheduleRow
                            index={index}
                            service={schedule.service}
                            professional={schedule.professional}
                          >
                            <FormField>
                              <Select
                                name={`schedules[${index}].professional`}
                                placeholder='Profissional...'
                                value={schedule.professional}
                                options={professionals.map (professional => ({
                                  label: professional.nickname,
                                  value: professional
                                }))}
                                onChange={(name, option) => {
                                  setFieldValue (`schedules[${index}].time`, null)
                                  handleProfessionalSelected (name, option, schedule.service)
                                }}
                                onBlur={setFieldTouched}
                                error={errors.schedules?.[index]?.professional}
                                touched={touched.schedules?.[index]?.professional}
                              />
                              {touched.schedules?.[index]?.professional && errors.schedules?.[index]?.professional && (
                                <WarningText>
                                  {errors.schedules?.[index]?.professional}
                                </WarningText>
                              )}
                            </FormField>
                            <FormField>
                              <Select
                                name={`schedules[${index}].service`}
                                placeholder='Serviço...'
                                value={schedule.service}
                                options={services}
                                onChange={(name, option) => {
                                  handleServiceSelected (name, schedule.professional, option)
                                }}
                                onBlur={setFieldTouched}
                                error={errors.schedules?.[index]?.service}
                                touched={touched.schedules?.[index]?.service}
                              />
                              {touched.schedules?.[index]?.service && errors.schedules?.[index]?.service && (
                                <WarningText>
                                  {errors.schedules[index].service}
                                </WarningText>
                              )}
                            </FormField>
                            {(index === 0 || (schedule.professional && schedule.service)) && (
                              <>
                                <FormField>
                                  <Select
                                    name={`schedules[${index}].time`}
                                    placeholder='Horário...'
                                    value={schedule.time}
                                    options={schedule.timeWindows}
                                    noOptionsMessage={getNoOptionsMessage (schedule.service)}
                                    onChange={setFieldValue}
                                    onBlur={setFieldTouched}
                                    error={errors.schedules?.[index]?.time}
                                    touched={touched.schedules?.[index]?.time}
                                  />
                                  {isTimeOutsideFreeWindows(schedule) && (
                                    <NoticeText>
                                      <MdInfoOutline size={16} color='#FFC100' style={{alignSelf: 'flex-start'}}/>
                                      Atenção! Este horário colide com outro agendamento ou passa do expediente
                                    </NoticeText>
                                  )}
                                </FormField>
                                {schedule.service && (
                                  <FormField>
                                    <NumberInput
                                      name={`schedules[${index}].duration`}
                                      value={schedule.duration}
                                      minValue={15}
                                      stepOnly={true}
                                      step={schedule.duration < 60 ? 15 : 30}
                                      onChange={(name, value) => {
                                        const newValue = schedule.duration === 60 && schedule.duration > value ? 45 : value
                                        setFieldValue (name, newValue)

                                        // A lista acompanha a duração, sem trocar o horário já escolhido
                                        if (schedule.professional) getTimeWindows(
                                          name, schedule.professional.value, schedule.service, newValue,
                                          { autoSelect: false }
                                        )
                                      }}
                                      style={{width: '100%'}}
                                    />
                                    <Label style={{cursor: 'default', marginBottom: -8}}>
                                      Duração (mins)
                                    </Label>
                                  </FormField>
                                )}
                              </>
                            )}
                            {index > 0 && (
                              <DangerButton onClick={() => handleDeleteSchedule (index)}>
                                <FaTrash color='#FFFFFF' size={12} style={{marginBottom: 4}}/>
                              </DangerButton>
                            )}
                          </ScheduleRow>
                        </Column>
                      ))}
                      <SecondaryButton
                        label='Adicionar serviço'
                        leftIcon={<FaPlus color='#FFC100' size={12} style={{marginBottom: 1}}/>}
                        onClick={handleAddSchedule}
                        style={{marginBottom: 15}}
                        width={160}
                      />
                      <FormField>
                        <CheckRow>
                          <CheckBox
                            id='addRecurrence'
                            style={{marginRight: -3}}
                            value={values.addRecurrence}
                            onValueChange={value => setFieldValue ('addRecurrence', value)}
                          />
                          <Label htmlFor='addRecurrence'>
                            Adicionar recorrência
                          </Label>
                        </CheckRow>
                      </FormField>
                      {values.addRecurrence && (
                        <Row style={{width: 640, alignSelf: 'center'}}>
                          <FormField>
                            <Label htmlFor='recurrence.frequency'>
                              Frequência
                            </Label>
                            <Select
                              id='recurrence.frequency'
                              name='recurrence.frequency'
                              placeholder='Recorrência...'
                              value={values.recurrence.frequency}
                              options={ScheduleService.FREQUENCY_OPTIONS}
                              onChange={setFieldValue}
                              onBlur={setFieldTouched}
                              containerStyles={{width: 220}}
                            />
                          </FormField>
                          <FormField>
                            <Label htmlFor='recurrence.type'>
                              Config. duração
                            </Label>
                            <Select
                              id='recurrence.type'
                              name='recurrence.type'
                              placeholder='Tipo...'
                              value={values.recurrence.type}
                              options={[
                                {label: 'Qtd. agendamentos', value: 'scheduleCount'},
                                {label: 'Data final', value: 'endDate'}
                              ]}
                              onChange={setFieldValue}
                              onBlur={setFieldTouched}
                              containerStyles={{width: 230}}
                            />
                          </FormField>
                          {values.recurrence.type?.value === 'scheduleCount' && (
                            <FormField>
                              <Label htmlFor='recurrence.scheduleCount'>
                                Qtd. agendamentos
                              </Label>
                              <NumberInput
                                id='recurrence.scheduleCount'
                                name='recurrence.scheduleCount'
                                placeholder='Qtd. agendamentos'
                                value={values.recurrence.scheduleCount}
                                minValue={1}
                                maxValue={99}
                                onChange={setFieldValue}
                              />
                            </FormField>
                          )}
                          {values.recurrence.type?.value === 'endDate' && (
                            <FormField>
                              <Label htmlFor='recurrence.endDate'>
                                Data final
                              </Label>
                              <DateInput
                                id='recurrence.endDate'
                                name='recurrence.endDate'
                                placeholder='Data final'
                                value={values.recurrence.endDate}
                                onChange={date => setFieldValue ('recurrence.endDate', date)}
                                onBlur={setFieldTouched}
                                style={{width: '100%'}}
                              />
                            </FormField>
                          )}
                        </Row>
                      )}
                      {errorMessage && (
                        <ErrorContainer>
                          <InfoText>{errorMessage}</InfoText>
                        </ErrorContainer>
                      )}
                      <AddCustomerModal
                        visible={showAddCustomerModal}
                        query={customerQuery}
                        onConfirm={customer => (
                          handleCustomerSelected ({ label: customer.name, value: customer.id })
                        )}
                        onClose={toggleAddCustomerModal}
                      />
                      {firstSchedule.customer && (
                        <EditCustomerModal
                          visible={showEditCustomerModal}
                          data={firstSchedule.customer.data}
                          onConfirm={customer => {
                            handleCustomerSelected ({
                              label: customer.name, value: customer.id, data: customer
                            })
                          }}
                          onClose={toggleEditCustomerModal}
                        />
                      )}
                      <OptionDialog
                        visible={showSkipDatesDialog}
                        title='Atenção!'
                        message={
                          <FormField>
                            <InfoText>
                              {values.recurrence.skipDates.length > 1 ? 'As seguintes datas já possuem ' : 'A seguinte data já possui '}
                              um agendamento Cutilei marcado no horário escolhido:
                            </InfoText>
                            {values.recurrence.skipDates.map ((date, index) => (
                              <b key={index}>
                                {index > 0 && ' - '}
                                {DateTime.fromISO (date).toFormat ('dd/MM/yyyy')}
                              </b>
                            ))}
                            <InfoText style={{marginTop: 5}}>
                              Os agendamentos que seriam marcados para essas datas serão
                              {values.recurrence.type.value === 'scheduleCount'
                                ? ' marcados nas semanas seguintes.' : ' desconsiderados.'
                              }
                            </InfoText>
                            <InfoText style={{marginTop: 5}}>
                              Deseja prosseguir com a criação dos agendamentos?
                            </InfoText>
                          </FormField>
                        }
                        options={[
                          {label: 'Sim', onClick: handleSubmit, dangerous: false},
                          {label: 'Não', onClick: resetSkipDates, dangerous: false}
                        ]}
                        onClose={resetSkipDates}
                        closeOnConfirm={false}
                      />
                    </Form>
                  </DialogBody>
                  <DialogFooter>
                    <MainButton
                      label='Agendar'
                      onClick={handleSubmit}
                      disabled={isSubmitting}
                      loading={isSubmitting}
                      width={160}
                    />
                  </DialogFooter>
                </>
              )
            }}
          </Formik>
        )}
      </DialogContent>
    </Dialog>
  )
}
